import React, { useState } from 'react';
import { Calendar, Upload, MessageCircle, Shield, ArrowRight, Award, User, Heart, Target, Lightbulb, Users, FileText, HelpCircle, FolderOpen, ChevronDown, ChevronUp, CreditCard, Mail } from 'lucide-react';
import FormMonivaeBooking from './FormMonivaeBooking';
import FormMonivaeReferral from './FormMonivaeReferral';
import FormMonivaeContact from './FormMonivaeContact';
import CrisisSupport from '../components/CrisisSupport';
import MonivaeFooter from '../components/MonivaeFooter';
import SEO from '../components/SEO';
import './Summary.css';

const Summary = () => {
  const [activeModal, setActiveModal] = useState(null);
  const [openFaqIndex, setOpenFaqIndex] = useState(null);

  const openModal = (modalName) => {
    setActiveModal(modalName);
    document.body.style.overflow = 'hidden';
  };

  const closeModal = () => {
    setActiveModal(null);
    setOpenFaqIndex(null);
    document.body.style.overflow = 'unset';
  };

  const toggleFAQ = (index) => {
    setOpenFaqIndex(openFaqIndex === index ? null : index);
  };

  return (
    <div className="summary-standalone" data-testid="monivae-portal">
      <SEO
        title="Clinical Minds Psychology at Monivae"
        description="Psychology support portal for Monivae College Hamilton. Confidential therapy and neurodevelopmental assessments for students and families. Clinical Psychologist (Registrar) Lachlan Beaton."
        canonicalUrl="https://www.clinicalminds.net/summary"
        ogImage="https://www.clinicalminds.net/og-image.jpg"
        noindex={true}
      />
      {/* Hero Section */}
      <div className="summary-hero">
        <div className="summary-hero-overlay"></div>
        <div className="summary-hero-inner">
          <div className="summary-header-logos">
            <img 
              src="/images/logos/clinical-minds-logo-white.png" 
              alt="Clinical Minds Psychology" 
              className="summary-logo summary-logo-left" 
            />
            <img 
              src="/images/logos/monivae-logo.png" 
              alt="Monivae College" 
              className="summary-logo summary-logo-right" 
            />
          </div>
          <div className="summary-header-divider"></div>
          <h1 className="summary-title">Monivae College</h1>
          <p className="summary-subtitle">Psychology Support Portal</p>
          <div className="summary-trust-badge">
            <Shield size={16} />
            <span>Confidential & Secure</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="summary-container">
        <div className="summary-intro">
          <p className="body-medium summary-welcome">Welcome to the Monivae psychology support portal. Get to know your psychologist and access the services you need.</p>
        </div>

        {/* About Your Psychologist Section */}
        <div className="summary-section">
          <span className="section-label">Your Psychologist</span>
          <h2 className="heading-2">About Your Psychologist</h2>
          <div className="summary-cards-grid" data-testid="about-cards">
            <button className="summary-card card" onClick={() => openModal('qualifications')}>
              <div className="summary-card-icon">
                <Award size={22} strokeWidth={1.5} />
              </div>
              <div className="summary-card-body">
                <h3 className="heading-3">Qualifications</h3>
                <p className="body-small">Education and credentials</p>
              </div>
              <ArrowRight className="summary-card-arrow" size={18} />
            </button>

            <button className="summary-card card" onClick={() => openModal('about')}>
              <div className="summary-card-icon">
                <User size={22} strokeWidth={1.5} />
              </div>
              <div className="summary-card-body">
                <h3 className="heading-3">About Me</h3>
                <p className="body-small">My story and background</p>
              </div>
              <ArrowRight className="summary-card-arrow" size={18} />
            </button>

            <button className="summary-card card" onClick={() => openModal('therapy')}>
              <div className="summary-card-icon">
                <Heart size={22} strokeWidth={1.5} />
              </div>
              <div className="summary-card-body">
                <h3 className="heading-3">Therapy Style</h3>
                <p className="body-small">My approach and methods</p>
              </div>
              <ArrowRight className="summary-card-arrow" size={18} />
            </button>
          </div>
        </div>

        {/* Important Information Section */}
        <div className="summary-section">
          <span className="section-label">Information</span>
          <h2 className="heading-2">Important Information</h2>
          <div className="summary-cards-grid" data-testid="info-cards">
            <button className="summary-card card" onClick={() => openModal('process')}>
              <div className="summary-card-icon">
                <FileText size={22} strokeWidth={1.5} />
              </div>
              <div className="summary-card-body">
                <h3 className="heading-3">Process and Fees</h3>
                <p className="body-small">How it works</p>
              </div>
              <ArrowRight className="summary-card-arrow" size={18} />
            </button>

            <button className="summary-card card" onClick={() => openModal('faqs')}>
              <div className="summary-card-icon">
                <HelpCircle size={22} strokeWidth={1.5} />
              </div>
              <div className="summary-card-body">
                <h3 className="heading-3">FAQs</h3>
                <p className="body-small">Common questions</p>
              </div>
              <ArrowRight className="summary-card-arrow" size={18} />
            </button>

            <button className="summary-card card" onClick={() => openModal('forms')}>
              <div className="summary-card-icon">
                <FolderOpen size={22} strokeWidth={1.5} />
              </div>
              <div className="summary-card-body">
                <h3 className="heading-3">Important Forms</h3>
                <p className="body-small">Required documents</p>
              </div>
              <ArrowRight className="summary-card-arrow" size={18} />
            </button>
          </div>
        </div>

        {/* Get Started Section */}
        <div className="summary-section">
          <span className="section-label">Get Started</span>
          <h2 className="heading-2">Get Started</h2>
          <div className="summary-cards-grid" data-testid="action-cards">
            <button className="summary-card card summary-card-action" onClick={() => openModal('booking')}>
              <div className="summary-card-icon summary-card-icon-action">
                <Calendar size={22} strokeWidth={1.5} />
              </div>
              <div className="summary-card-body">
                <h3 className="heading-3">Request an Appointment</h3>
                <p className="body-small">Schedule a session</p>
              </div>
              <ArrowRight className="summary-card-arrow" size={18} />
            </button>

            <button className="summary-card card summary-card-action" onClick={() => openModal('referral')}>
              <div className="summary-card-icon summary-card-icon-action">
                <Upload size={22} strokeWidth={1.5} />
              </div>
              <div className="summary-card-body">
                <h3 className="heading-3">Upload Referral</h3>
                <p className="body-small">Submit documents securely</p>
              </div>
              <ArrowRight className="summary-card-arrow" size={18} />
            </button>

            <button className="summary-card card summary-card-action" onClick={() => openModal('contact')}>
              <div className="summary-card-icon summary-card-icon-action">
                <MessageCircle size={22} strokeWidth={1.5} />
              </div>
              <div className="summary-card-body">
                <h3 className="heading-3">Contact Us</h3>
                <p className="body-small">Send us a message</p>
              </div>
              <ArrowRight className="summary-card-arrow" size={18} />
            </button>
          </div>
        </div>

        {/* Crisis Support */}
        <div className="crisis-support-highlight">
          <CrisisSupport />
        </div>
      </div>

      {/* Monivae Footer */}
      <MonivaeFooter />

      {/* ── Modals ── */}

      {activeModal === 'booking' && (
        <div className="modal-overlay active" onClick={closeModal}>
          <div className="modal-content-wrapper" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">&times;</button>
            <div className="modal-form-container">
              <FormMonivaeBooking />
            </div>
          </div>
        </div>
      )}

      {activeModal === 'referral' && (
        <div className="modal-overlay active" onClick={closeModal}>
          <div className="modal-content-wrapper" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">&times;</button>
            <div className="modal-form-container">
              <FormMonivaeReferral />
            </div>
          </div>
        </div>
      )}

      {activeModal === 'contact' && (
        <div className="modal-overlay active" onClick={closeModal}>
          <div className="modal-content-wrapper" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">&times;</button>
            <div className="modal-form-container">
              <FormMonivaeContact />
            </div>
          </div>
        </div>
      )}

      {/* Qualifications Modal */}
      {activeModal === 'qualifications' && (
        <div className="modal-overlay active" onClick={closeModal}>
          <div className="modal-content-wrapper modal-content-info" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">&times;</button>
            <div className="modal-info-container">
              <span className="section-label">Credentials</span>
              <h2 className="heading-2 modal-info-title">Qualifications</h2>
              <div className="modal-info-body">
                <h3>Psychology Qualifications:</h3>
                <ul className="qualifications-list">
                  <li>Masters in Psychology (Clinical) - University of New England</li>
                  <li>Bachelor of Psychological Science (Honours) - Australian Catholic University</li>
                  <li>Graduate Diploma of Psychological Science - Deakin University</li>
                </ul>

                <h3>Other Qualifications:</h3>
                <ul className="qualifications-list">
                  <li>Bachelor of Commerce - The University of Melbourne</li>
                  <li>Diploma in Arts (Political Science) - The University of Melbourne</li>
                  <li>Certified Practicing Accountant (CPA) - CPA Australia</li>
                </ul>

                <div className="university-logos">
                  <img src="/images/logos/unimelb-logo.png" alt="University of Melbourne" className="university-logo" />
                  <img src="/images/logos/deakin-logo.png" alt="Deakin University" className="university-logo" />
                  <img src="/images/logos/acu-logo.png" alt="Australian Catholic University" className="university-logo" />
                  <img src="/images/logos/une-logo.png" alt="University of New England" className="university-logo" />
                  <img src="/images/logos/cpa-australia-logo.png" alt="CPA Australia" className="university-logo" />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* About Me Modal */}
      {activeModal === 'about' && (
        <div className="modal-overlay active" onClick={closeModal}>
          <div className="modal-content-wrapper modal-content-info" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">&times;</button>
            <div className="modal-info-container">
              <span className="section-label">Background</span>
              <h2 className="heading-2 modal-info-title">About Me</h2>
              <div className="modal-info-body">
                <div className="about-section">
                  <div className="about-photo-column">
                    <img src="/images/photos/rural-victoria.jpg" alt="Victorian farm landscape" className="about-thumbnail" />
                    <p className="photo-caption">The farm I grew up on, in western Victoria</p>
                  </div>
                  <div className="about-text-column">
                    <p>I grew up in the far west of country Victoria, on a farm with my parents and three brothers. Sport was my life when I was young, and being healthy and fit still dominates my life now. As a young adult, I studied at The University of Melbourne and then worked in Melbourne and Sydney, before deciding to call the mid-north coast of NSW, home.</p>
                  </div>
                </div>

                <div className="about-section">
                  <div className="about-photo-column">
                    <img src="/images/photos/rural-farm.jpg" alt="Rural primary school" className="about-thumbnail" />
                    <p className="photo-caption">My primary school, in a paddock, population 30</p>
                  </div>
                  <div className="about-text-column">
                    <p>Having worked in finance and media when I was younger, I was drawn to psychology by my fascination with how people behave, and a desire to share with others.</p>
                    <p>I became a psychologist to give people the support I wanted when I was younger. As someone who was born in rural Australia, I am passionate about providing support to those who aren't always offered it and to those who, like me, come from the regions.</p>
                  </div>
                </div>

                <div className="about-section">
                  <div className="about-photo-column">
                    <img src="/images/photos/coastal-path.jpg" alt="Coastal path" className="about-thumbnail" />
                    <p className="photo-caption">The beautiful mid north coast</p>
                  </div>
                  <div className="about-text-column">
                    <p>For me, therapy isn't about having all the answers. It's about creating a space where you feel heard, understood, and empowered to find your own path forward.</p>
                    <p>When I'm not in session, you'll probably find me exploring the mid-north coast or on the beach and I love to travel and explore. My genuine curiosity and interest in others is what defines me in my psychological practice.</p>
                  </div>
                </div>

                <div className="about-section">
                  <div className="about-photo-column">
                    <img src="/images/photos/lachlan-coastal.jpg" alt="Lachlan Beaton, Clinical Psychologist (Registrar), at the beach" className="about-thumbnail" />
                    <p className="photo-caption">Where I love to spend time</p>
                  </div>
                  <div className="about-text-column">
                    <p>For me, therapy isn't about having all the answers. It's about creating a space where you feel heard, understood, and empowered to find your own path forward. Most of my free time is spent near the water, taking photos of the beautiful mid north coast – a place where answers often come to me!</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Therapy Style Modal */}
      {activeModal === 'therapy' && (
        <div className="modal-overlay active" onClick={closeModal}>
          <div className="modal-content-wrapper modal-content-info" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">&times;</button>
            <div className="modal-info-container">
              <span className="section-label">Approach</span>
              <h2 className="heading-2 modal-info-title">Therapy Style</h2>
              <div className="modal-info-body">
                <h3>My Approach</h3>
                <div className="therapy-approach">
                  <div className="approach-item">
                    <div className="approach-icon"><Heart size={28} strokeWidth={1.5} /></div>
                    <div className="approach-content">
                      <h4>Genuine Connection</h4>
                      <p>I believe the relationship between us is just as important as any technique or strategy we use.</p>
                    </div>
                  </div>
                  <div className="approach-item">
                    <div className="approach-icon"><Target size={28} strokeWidth={1.5} /></div>
                    <div className="approach-content">
                      <h4>Your Pace, Your Goals</h4>
                      <p>We'll work at a speed that feels right for you, focusing on what matters most in your life.</p>
                    </div>
                  </div>
                  <div className="approach-item">
                    <div className="approach-icon"><Lightbulb size={28} strokeWidth={1.5} /></div>
                    <div className="approach-content">
                      <h4>Practical & Evidence-Based</h4>
                      <p>I use approaches that are backed by science, but always tailored to fit who you are as a person.</p>
                    </div>
                  </div>
                  <div className="approach-item">
                    <div className="approach-icon"><Users size={28} strokeWidth={1.5} /></div>
                    <div className="approach-content">
                      <h4>No Judgment Zone</h4>
                      <p>My office (virtual or physical) is a place where you can be completely yourself.</p>
                    </div>
                  </div>
                </div>

                <h3>Evidence-Based Approaches</h3>
                <p>I draw from a range of therapeutic approaches, tailored to your unique needs:</p>
                <div className="therapy-list">
                  <div className="therapy-tag">Schema Therapy</div>
                  <div className="therapy-tag">Dialectical Behavior Therapy (DBT)</div>
                  <div className="therapy-tag">Person Centred Therapy</div>
                  <div className="therapy-tag">Attachment Based Therapy</div>
                  <div className="therapy-tag">Cognitive Behavioral Therapy (CBT)</div>
                  <div className="therapy-tag">Motivational Interviewing</div>
                  <div className="therapy-tag">Acceptance and Commitment Therapy</div>
                  <div className="therapy-tag">Trauma Informed CBT</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Process Modal */}
      {activeModal === 'process' && (
        <div className="modal-overlay active" onClick={closeModal}>
          <div className="modal-content-wrapper modal-content-info" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">&times;</button>
            <div className="modal-info-container">
              <span className="section-label">Process</span>
              <h2 className="heading-2 modal-info-title">Process and Fees</h2>
              <div className="modal-info-body">
                <div className="process-card">
                  <div className="process-card-icon"><Mail size={28} strokeWidth={1.5} /></div>
                  <div className="process-card-content">
                    <h4>Contact the Wellbeing Team</h4>
                    <p>
                      If you have a child or someone you care for who would like to see a psychologist either for therapy or for an assessment (ADHD, Autism Spectrum Disorder, Cognitive Assessment etc) you can email the Monivae Wellbeing team <a href="mailto:wellbeinghub@monivae.vic.edu.au">here</a>.
                    </p>
                  </div>
                </div>

                <div className="process-card">
                  <div className="process-card-icon"><Calendar size={28} strokeWidth={1.5} /></div>
                  <div className="process-card-content">
                    <h4>Direct Booking</h4>
                    <p>
                      Alternatively, please follow the "request appointment" or "contact us" tab of this website and Lachlan Beaton will be in direct contact with you.
                    </p>
                  </div>
                </div>

                <div className="process-card">
                  <div className="process-card-icon"><CreditCard size={28} strokeWidth={1.5} /></div>
                  <div className="process-card-content">
                    <h4>Session Fees</h4>
                    <p>
                      <strong>Therapy:</strong> No fee if you have a Mental Health Treatment Plan. Without a MHTP, fees are $100. Payment can be made online with a credit card or in person with EFTPOS.
                    </p>
                    
                    <h5>Cognitive Assessments</h5>
                    <ul>
                      <li>Cognitive Assessment: $1,120</li>
                      <li>Cognitive and Educational Assessment (for learning difficulties): $1,400</li>
                    </ul>

                    <h5>ADHD Assessments</h5>
                    <ul>
                      <li>ADHD Assessment (includes full cognitive assessment): $1,400</li>
                      <li>ADHD Assessment (includes full cognitive assessment and learning difficulty assessment): $1,540</li>
                    </ul>

                    <h5>Autism Spectrum Assessments</h5>
                    <ul>
                      <li>Autism Spectrum Disorder Assessment (includes full cognitive assessment): $1,540</li>
                      <li>Autism Spectrum Disorder Assessment (includes full cognitive assessment and ADHD screen): $1,750</li>
                    </ul>
                  </div>
                </div>

                <div className="process-card">
                  <div className="process-card-icon"><MessageCircle size={28} strokeWidth={1.5} /></div>
                  <div className="process-card-content">
                    <h4>Still Not Sure?</h4>
                    <p>
                      Please feel free to reach out to <a href="mailto:contact@clinicalminds.net">Lachlan</a> or <a href="mailto:wellbeinghub@monivae.vic.edu.au">The Wellbeing Team</a>.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* FAQs Modal */}
      {activeModal === 'faqs' && (
        <div className="modal-overlay active" onClick={closeModal}>
          <div className="modal-content-wrapper modal-content-info" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">&times;</button>
            <div className="modal-info-container">
              <span className="section-label">Support</span>
              <h2 className="heading-2 modal-info-title">FAQs</h2>
              <div className="modal-info-body">
                {/* Booking & Appointments */}
                <h3 className="faq-category-title">
                  <Calendar size={20} strokeWidth={1.5} />
                  Booking & Appointments
                </h3>
                {[
                  { 
                    question: 'How do I book an appointment?', 
                    answer: 'If you have a child or someone you care for who would like to see a psychologist either for therapy or for an assessment (ADHD, Autism Spectrum Disorder, Cognitive Assessment etc) you can email the Monivae Wellbeing team here – wellbeinghub@monivae.vic.edu.au. Alternatively, please follow the "request appointment" or "contact us" tab of this website and Lachlan Beaton will be in direct contact with you.',
                    hasLink: true
                  },
                  { question: 'How do I schedule a follow up appointment?', answer: 'We can schedule your appointment after your session, together, or you can phone, email or text us and we can arrange it for you.' },
                  { question: 'What happens after my first appointment is booked in?', answer: 'You will be emailed intake forms and other important information to review. These forms can be completed online and should be completed prior to our appointment, so we can be fully prepared and make our time together efficient. However, if you do have questions, we can do the forms together, during the session (just note, it may take up to 20 minutes to complete). Importantly, the forms do need to be finished before we start our first session.' },
                  { question: 'Will I receive reminders?', answer: 'You will be sent an SMS and an email 3 days prior to your appointment to remind you of the time and date. If you can respond to the SMS to confirm the booking, that would be great.' },
                  { question: 'Can I change my booking?', answer: 'Yes, please email the wellbeing team (wellbeinghub@monivae.vic.edu.au) and they will help you out.', hasLink: true },
                  { question: 'What if I need to cancel or reschedule?', answer: '24 hours\' notice is required for cancellations to allow the time slot to be offered to other clients. If you cancel within 24 hours, you will be charged the full fee.' }
                ].map((faq, idx) => (
                  <div key={idx} className="faq-item">
                    <button className="faq-question" onClick={() => toggleFAQ(`booking-${idx}`)}>
                      <span>{faq.question}</span>
                      {openFaqIndex === `booking-${idx}` ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {openFaqIndex === `booking-${idx}` && (
                      <div className="faq-answer">
                        {faq.question === 'How do I book an appointment?' ? (
                          <p>If you have a child or someone you care for who would like to see a psychologist either for therapy or for an assessment (ADHD, Autism Spectrum Disorder, Cognitive Assessment etc) you can email the Monivae Wellbeing team <a href="mailto:wellbeinghub@monivae.vic.edu.au">here</a>. Alternatively, please follow the "request appointment" or "contact us" tab of this website and Lachlan Beaton will be in direct contact with you.</p>
                        ) : faq.question === 'Can I change my booking?' ? (
                          <p>Yes, please email the wellbeing team <a href="mailto:wellbeinghub@monivae.vic.edu.au">here</a> and they will help you out.</p>
                        ) : (
                          <p>{faq.answer}</p>
                        )}
                      </div>
                    )}
                  </div>
                ))}

                {/* Getting Started */}
                <h3 className="faq-category-title">
                  <Lightbulb size={20} strokeWidth={1.5} />
                  Getting Started
                </h3>
                {[
                  { question: 'What should I expect in my first session?', answer: 'The first session focuses on understanding your concerns, goals, and developing a treatment plan together.' },
                  { question: 'I don\'t know if therapy is right for me!', answer: 'There is never an obligation to continue once you start. If it doesn\'t feel right, we can work together to find someone else or determine if it\'s not what you\'re looking for.' },
                  { question: 'How do I prepare for my first session?', answer: 'Complete any intake forms sent to you beforehand. Think about what you\'d like to discuss and any questions you have. Arrive 10-15 minutes early for face-to-face sessions. For telehealth, make sure you have a private, quiet space with a good internet connection.' },
                  { question: 'What sort of paperwork do I need to fill out?', answer: 'You\'ll be emailed various forms after your first appointment is booked in. These forms help us understand more about you and they cover important topics like privacy and consent. Consent is really important to us, as we want to make sure you understand the process of psychology, what data we keep, what we can and can\'t disclose and how we treat your confidential information.' }
                ].map((faq, idx) => (
                  <div key={idx} className="faq-item">
                    <button className="faq-question" onClick={() => toggleFAQ(`getting-started-${idx}`)}>
                      <span>{faq.question}</span>
                      {openFaqIndex === `getting-started-${idx}` ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {openFaqIndex === `getting-started-${idx}` && (
                      <div className="faq-answer"><p>{faq.answer}</p></div>
                    )}
                  </div>
                ))}

                {/* Sessions & Treatment */}
                <h3 className="faq-category-title">
                  <MessageCircle size={20} strokeWidth={1.5} />
                  Sessions & Treatment
                </h3>
                {[
                  { question: 'How long are sessions and how often should I attend?', answer: 'Therapy sessions are typically 50 minutes long. The frequency of sessions is totally up to you! Assessment sessions vary, but you can expect to spend the best part of a full day with me.' },
                  { question: 'Is everything I share confidential?', answer: 'Yes, everything discussed in sessions is confidential. However, there are limited circumstances where confidentiality may be need to be broken, which will be explained during your first meeting. All of your data is kept securely, in line with Australian privacy laws and requirements. To ensure you fully understand how confidentiality works and any limits to this, you will be emailed a form called "informed consent" and this is something we take very seriously. We will talk you through this at your first appointment, to make sure you understand it. Consent is something we will re-visit at least annually or at anytime you wish.' },
                  { question: 'What qualifications does Lachlan have?', answer: 'Lachlan holds a Master of Clinical Psychology, Bachelor of Psychological Science (Honours), Graduate Diploma of Psychological Science, and a Bachelor of Commerce with a Diploma of Arts. He is a registered psychologist with the Australian Health Practitioner Regulation Agency (AHPRA) and a Clinical Psychology Registrar.' },
                  { question: 'What types of therapy do you offer?', answer: 'The practice offers several evidence-based therapeutic approaches tailored to your needs, including: Schema Therapy, Dialectical Behaviour Therapy (DBT), Motivational Interviewing, Cognitive Behavioural Therapy (CBT), Mindfulness Based Interventions, Acceptance and Commitment Therapy (ACT), and Trauma Informed CBT.' },
                  { question: 'Is it face to face or telehealth?', answer: 'Therapy is mostly telehealth and assessments are all face to face. The Wellbeing team have a dedicated space for students to engage in the telehealth service.' }
                ].map((faq, idx) => (
                  <div key={idx} className="faq-item">
                    <button className="faq-question" onClick={() => toggleFAQ(`sessions-${idx}`)}>
                      <span>{faq.question}</span>
                      {openFaqIndex === `sessions-${idx}` ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {openFaqIndex === `sessions-${idx}` && (
                      <div className="faq-answer"><p>{faq.answer}</p></div>
                    )}
                  </div>
                ))}

                {/* Fees */}
                <h3 className="faq-category-title">
                  <CreditCard size={20} strokeWidth={1.5} />
                  Fees
                </h3>
                {[
                  { 
                    question: 'What are your session fees?', 
                    answer: 'Therapy – no fee if you have a Mental Health Treatment Plan. Without a MHTP, fees are $100. Payment can be made online with a credit card or in person with EFTPOS\n\nAssessments:\n• ADHD Assessment (includes full cognitive assessment): $1,400\n• Autism Spectrum Disorder Assessment (includes full cognitive assessment): $1,540\n• Cognitive Assessment: $1,600\n• Cognitive and Educational Assessment (for learning difficulties): $1,260\n• Autism & ADHD & Cognitive Assessment: $1,750' 
                  }
                ].map((faq, idx) => (
                  <div key={idx} className="faq-item">
                    <button className="faq-question" onClick={() => toggleFAQ(`fees-${idx}`)}>
                      <span>{faq.question}</span>
                      {openFaqIndex === `fees-${idx}` ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {openFaqIndex === `fees-${idx}` && (
                      <div className="faq-answer" style={{ whiteSpace: 'pre-line' }}><p>{faq.answer}</p></div>
                    )}
                  </div>
                ))}

                {/* Referrals */}
                <h3 className="faq-category-title">
                  <FileText size={20} strokeWidth={1.5} />
                  Referrals (Mental Health Treatment Plan)
                </h3>
                {[
                  { question: 'What is a Mental Health Treatment Plan?', answer: 'A Mental Health Treatment Plan is a document created by a general practitioner (GP) in Australia that outlines a patient\'s mental health needs, goals, and a treatment plan. It allows eligible patients to access Medicare rebates for up to 10 individual psychological sessions per calendar year. This plan acts as a referral, ensuring that you can receive subsidised sessions with a registered psychologist. The primary purpose of the plan is to make mental health support more affordable and accessible for Australians.' },
                  { question: 'How do I get a Mental Health Treatment Plan?', answer: 'Your GP can help you with this. If you would like to access psychology with a Mental Health Treatment Plan, you may wish visit your GP and talk to them about this. Your GP will assess your needs and, if appropriate, provide a Mental Health Treatment Plan. This plan allows you to claim Medicare rebates for up to 10 sessions per calendar year. You need this plan to be eligible for the medicare rebate.' },
                  { question: 'Do I need to have a copy of the Mental Health Treatment Plan and/or send it to you?', answer: 'Your GP usually sends us a copy of the plan, but if we don\'t receive it, we may ask you to follow this up with them.' },
                  { question: 'Does the Mental Health Treatment Plan need to be reviewed during the year?', answer: 'Yes, after your first 6 sessions, I will write to your GP, updating them on your progress. You will then need to book an appointment to see them, so they can request your final 4 appointments for the year.' }
                ].map((faq, idx) => (
                  <div key={idx} className="faq-item">
                    <button className="faq-question" onClick={() => toggleFAQ(`referrals-${idx}`)}>
                      <span>{faq.question}</span>
                      {openFaqIndex === `referrals-${idx}` ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {openFaqIndex === `referrals-${idx}` && (
                      <div className="faq-answer"><p>{faq.answer}</p></div>
                    )}
                  </div>
                ))}

                {/* Privacy */}
                <h3 className="faq-category-title">
                  <Shield size={20} strokeWidth={1.5} />
                  Privacy
                </h3>
                {[
                  { question: 'Is everything I share in sessions confidential?', answer: 'Yes, everything discussed in sessions is confidential. However, there are limited circumstances where confidentiality may be need to be broken, which will be explained during your first meeting. All of your data is kept securely, in line with Australian privacy laws and requirements. To ensure you fully understand how confidentiality works and any limits to this, you will be emailed a form called "informed consent" and this is something we take very seriously. We will talk you through this at your first appointment, to make sure you understand it. Consent is something we will re-visit at least annually or at anytime you wish.' },
                  { question: 'How is my personal information stored and protected?', answer: 'All client records are stored securely using encrypted digital systems that comply with Australian privacy legislation. Physical documents are kept in locked filing cabinets in a secure location. Access to your information is restricted to authorised personnel only.' },
                  { question: 'Who has access to my personal information?', answer: 'Only your psychologist and essential administrative staff have access to your personal information. Information is never shared with third parties without your explicit written consent, except in very limited legal circumstances that will be explained to you.' },
                  { question: 'Can I access my own records?', answer: 'Yes, you have the right to access your own psychological records. You can request to view or obtain copies of your records by contacting the practice. There may be a small administrative fee for copying services.' },
                  { question: 'How long are my records kept?', answer: 'Client records are retained for a minimum of 7 years after your last session, as required by professional guidelines and Australian law. After this period, records are securely destroyed.' },
                  { question: 'What happens to my information if I want to transfer to another psychologist?', answer: 'With your written consent, we can transfer relevant information to your new psychologist to ensure continuity of care. You have full control over what information is shared and with whom.' }
                ].map((faq, idx) => (
                  <div key={idx} className="faq-item">
                    <button className="faq-question" onClick={() => toggleFAQ(`privacy-${idx}`)}>
                      <span>{faq.question}</span>
                      {openFaqIndex === `privacy-${idx}` ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                    </button>
                    {openFaqIndex === `privacy-${idx}` && (
                      <div className="faq-answer"><p>{faq.answer}</p></div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Important Forms Modal */}
      {activeModal === 'forms' && (
        <div className="modal-overlay active" onClick={closeModal}>
          <div className="modal-content-wrapper modal-content-info" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close-btn" onClick={closeModal} aria-label="Close modal">&times;</button>
            <div className="modal-info-container">
              <span className="section-label">Documents</span>
              <h2 className="heading-2 modal-info-title">Important Forms</h2>
              <div className="modal-info-body">
                <p>Download and review these important documents. Click any link to open the document in a new window.</p>
                
                <div className="forms-list">
                  <a href="/Clinical_Minds_Privacy_Policy.pdf" target="_blank" rel="noopener noreferrer" className="form-link">
                    <div className="form-link-icon"><FileText size={24} strokeWidth={1.5} /></div>
                    <div className="form-link-content">
                      <h4>Privacy Policy</h4>
                      <p>Our commitment to protecting your information</p>
                    </div>
                    <ArrowRight className="form-link-arrow" size={20} />
                  </a>

                  <a href="/Clinical_Minds_Terms_and_Conditions.pdf" target="_blank" rel="noopener noreferrer" className="form-link">
                    <div className="form-link-icon"><FileText size={24} strokeWidth={1.5} /></div>
                    <div className="form-link-content">
                      <h4>Terms and Conditions</h4>
                      <p>Service agreement and policies</p>
                    </div>
                    <ArrowRight className="form-link-arrow" size={20} />
                  </a>

                  <a href="/Clinical_Minds_New_Client_Intake_and_Consent.pdf" target="_blank" rel="noopener noreferrer" className="form-link">
                    <div className="form-link-icon"><FileText size={24} strokeWidth={1.5} /></div>
                    <div className="form-link-content">
                      <h4>New Client Intake</h4>
                      <p>Complete this form before your first session</p>
                    </div>
                    <ArrowRight className="form-link-arrow" size={20} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Summary;
